import { get, isEmpty, omit } from 'lodash'
import {
  LOAD_COMPARABLE_PROPERTIES_FAILED,
  LOAD_COMPARABLE_PROPERTIES_FINISHED,
  LOAD_COPYRIGHT_FAILED,
  LOAD_COPYRIGHT_FINISHED,
  LOAD_DISCLAIMER_FAILED,
  LOAD_DISCLAIMER_FINISHED,
  LOAD_LOCAL_SCHOOLS_FAILED,
  LOAD_LOCAL_SCHOOLS_FINISHED,
  LOAD_MAP_FAILED,
  LOAD_MAP_FINISHED,
  LOAD_MARKET_INSIGHTS_FAILED,
  LOAD_MARKET_INSIGHTS_FINISHED,
  LOAD_PROPERTY_PHOTO_FAILED,
  LOAD_PROPERTY_PHOTO_FINISHED,
  LOAD_PROPERTY_SUMMARY_FAILED,
  LOAD_PROPERTY_SUMMARY_FINISHED,
  LOAD_PROPERTY_TIMELINE_FAILED,
  LOAD_PROPERTY_TIMELINE_FINISHED,
  LOAD_RATING_VALUATION_FAILED,
  LOAD_RATING_VALUATION_FINISHED,
  LOAD_RECORD_OF_TITLE_DETAILS_FAILED,
  LOAD_RECORD_OF_TITLE_DETAILS_FINISHED,
  LOAD_VALUATION_ESTIMATES_FAILED,
  LOAD_VALUATION_ESTIMATES_FINISHED,
  RESET_COMPARABLE_PROPERTIES,
  RESET_LOCAL_SCHOOLS,
  RESET_MAP,
  RESET_MARKET_INSIGHTS,
  RESET_PROPERTY_PHOTO,
  RESET_PROPERTY_SUMMARY,
  RESET_PROPERTY_TIMELINE,
  RESET_RATING_VALUATION,
  RESET_RECORD_OF_TITLE_DETAILS,
  RESET_VALUATION_ESTIMATES
} from 'store/actions/clapiAction'
import {
  COMPARABLE_PROPERTIES_PANEL_ERROR,
  COPYRIGHT_PANEL_ERROR,
  DISCLAIMER_PANEL_ERROR,
  LOCAL_SCHOOLS_PANEL_ERROR,
  MAP_PANEL_ERROR,
  MARKET_INSIGHTS_NOT_AVAILABLE,
  MARKET_INSIGHTS_PANEL_ERROR,
  PROPERTY_PHOTO_PANEL_ERROR,
  PROPERTY_TIMELINE_PANEL_ERROR,
  RATING_VALUATION_PANEL_ERROR,
  TITLE_DETAILS_PANEL_ERROR,
  VALUATION_ESTIMATES_PANEL_ERROR
} from 'constants/errorMessageConstant'

import { RESPONSE_RESPONSE_MESSAGE } from 'utils/constants'

const DEFAULT_STATUS = {
  isError: false,
  loading: true,
  msg: null
}

const DEFAULT_DATA_OBJECT = {
  data: {},
  status: DEFAULT_STATUS
}

const DEFAULT_DATA_ARRAY = {
  data: [],
  status: DEFAULT_STATUS
}

const INITIAL_STATE = {
  propertyPhoto: DEFAULT_DATA_OBJECT,
  propertySummary: DEFAULT_DATA_OBJECT,
  valuationEstimates: DEFAULT_DATA_OBJECT,
  recordOfTitleDetails: DEFAULT_DATA_OBJECT,
  ratingValuation: DEFAULT_DATA_OBJECT,
  disclaimer: DEFAULT_DATA_ARRAY,
  copyright: DEFAULT_DATA_ARRAY,
  propertyTimeline: DEFAULT_DATA_OBJECT,
  comparableProperties: DEFAULT_DATA_OBJECT,
  localSchools: DEFAULT_DATA_OBJECT,
  marketInsights: DEFAULT_DATA_OBJECT,
  mapPropertyCoordinates: DEFAULT_DATA_OBJECT
}

function getRecordTitleDetails(action, state) {
  const { titles, errors } = action.payload
  const isError = !isEmpty(errors) || get(state, 'recordOfTitleDetails.status.isError')
  const msg = isError ? RATING_VALUATION_PANEL_ERROR : get(state, 'recordOfTitleDetails.status.msg')
  const latestTitleList = titles.sort((a, b) => new Date(b.titleDescription.issueDate) - new Date(a.titleDescription.issueDate))

  const titleDescription = latestTitleList && latestTitleList.length > 0 ? latestTitleList[0].titleDescription : null
  const legalDescription = latestTitleList && latestTitleList.length > 0 ? latestTitleList[0].legalDescriptions : null

  return {
    ...state,
    recordOfTitleDetails: {
      data: {
        title: titleDescription ? titleDescription.reference : null,
        type: titleDescription ? titleDescription.type : null,
        legalDescription: legalDescription && legalDescription.length > 0 ? legalDescription[0].description : null,
        dateIssued: titleDescription ? titleDescription.issueDate : null,
        priorTitle: titleDescription ? titleDescription.priorTitleReferences[0] : null,
        share: legalDescription && legalDescription.length > 0 ? legalDescription[0].estateShare : null
      },
      status: {
        ...state.recordOfTitleDetails.status,
        msg,
        isError,
        loading: false,
        errors
      }
    }
  }
}

function getRatingValuation(action, state) {
  const { siteValueList, errors } = action.payload
  const latestSiteValueList = siteValueList.sort((a, b) => new Date(b.date) - new Date(a.date))
  const isError = !isEmpty(errors) || get(state, 'ratingValuation.status.isError')
  const msg = isError ? RATING_VALUATION_PANEL_ERROR : get(state, 'ratingValuation.status.msg')

  const capitalValue = latestSiteValueList && latestSiteValueList.find((siteValue) => siteValue.type === 'CV')
  const improvementValue = latestSiteValueList && latestSiteValueList.find((siteValue) => siteValue.type === 'IV')
  const landValue = latestSiteValueList && latestSiteValueList.find((siteValue) => siteValue.type === 'UCV')
  const valuationDate = latestSiteValueList && latestSiteValueList[0]

  return {
    ...state,
    ratingValuation: {
      data: {
        capitalValue: capitalValue ? capitalValue.value : null,
        improvementValue: improvementValue ? improvementValue.value : null,
        landValue: landValue ? landValue.value : null,
        valuationDate: valuationDate ? valuationDate.date : null
      },
      status: {
        ...state.ratingValuation.status,
        isError,
        loading: false,
        errors,
        msg
      }
    }
  }
}

export default function clapiReducer(state = INITIAL_STATE, action = {}) {
  switch (action.type) { // NOSONAR
    case LOAD_PROPERTY_PHOTO_FINISHED: {
      const photos = action.payload
        .sort((a, b) => new Date(b.scanDate) - new Date(a.scanDate))
        .reduce((acc, { digitalAssetType, isDefaultPhoto, largePhotoUrl, thumbnailPhotoUrl, scanDate }) => [...acc, {
          digitalAssetType,
          isDefaultPhoto,
          photoUrl: largePhotoUrl,
          thumbnailPhotoUrl,
          scanDate
        }], [])

      return {
        ...state,
        propertyPhoto: {
          data: { photos },
          status: {
            ...state.propertyPhoto.status,
            loading: false
          }
        }
      }
    }
    case LOAD_PROPERTY_PHOTO_FAILED:
      return {
        ...state,
        propertyPhoto: {
          ...state.propertyPhoto,
          status: {
            isError: true,
            loading: !state.propertyPhoto.status.loading,
            msg: PROPERTY_PHOTO_PANEL_ERROR
          }
        }
      }
    case LOAD_PROPERTY_SUMMARY_FINISHED: {
      const { errors, ...rest } = action.payload
      const isError = errors && errors.length > 4

      return {
        ...state,
        propertySummary: {
          data: rest,
          status: {
            isError,
            loading: false,
            errors
          }
        }
      }
    }
    case LOAD_PROPERTY_SUMMARY_FAILED:
      return {
        ...state,
        propertySummary: {
          data: {},
          status: {
            isError: true,
            loading: false
          }
        }
      }
    case LOAD_VALUATION_ESTIMATES_FINISHED: {
      const { estimatedValue, estimatedRental, errors } = action.payload

      return {
        ...state,
        valuationEstimates: {
          data: {
            estimatedValue,
            estimatedRental: estimatedRental && { ...omit(estimatedRental, ['rentalAvmRunDate', 'rentalAvmScore', 'rentalAvmYieldFsdScore']) },
            errors
          },
          status: {
            ...state.valuationEstimates.status,
            loading: false,
            msg: null
          }
        }
      }
    }
    case LOAD_VALUATION_ESTIMATES_FAILED:
      return {
        ...state,
        valuationEstimates: {
          ...state.valuationEstimates,
          status: {
            isError: true,
            loading: !state.valuationEstimates.status.loading,
            msg: VALUATION_ESTIMATES_PANEL_ERROR
          }
        }
      }
    case LOAD_RECORD_OF_TITLE_DETAILS_FINISHED: {
      return getRecordTitleDetails(action, state)
    }
    case LOAD_RECORD_OF_TITLE_DETAILS_FAILED:
      return {
        ...state,
        recordOfTitleDetails:
        {
          data: {},
          status: {
            isError: true,
            loading: !get(state, 'recordOfTitleDetails.status.loading'),
            msg: TITLE_DETAILS_PANEL_ERROR
          }
        }
      }
    case LOAD_RATING_VALUATION_FINISHED: {
      return getRatingValuation(action, state)
    }
    case LOAD_RATING_VALUATION_FAILED:
      return {
        ...state,
        ratingValuation: {
          ...state.ratingValuation,
          status: {
            isError: true,
            loading: !get(state, 'ratingValuation.status.loading'),
            msg: RATING_VALUATION_PANEL_ERROR
          }
        }
      }
    case RESET_PROPERTY_PHOTO:
      return {
        ...state,
        propertyPhoto: {
          ...INITIAL_STATE.propertyPhoto
        }
      }
    case RESET_PROPERTY_SUMMARY:
      return {
        ...state,
        propertySummary: {
          ...INITIAL_STATE.propertySummary
        }
      }
    case RESET_VALUATION_ESTIMATES:
      return {
        ...state,
        valuationEstimates: {
          ...INITIAL_STATE.valuationEstimates
        }
      }
    case RESET_RATING_VALUATION:
      return {
        ...state,
        ratingValuation: {
          ...INITIAL_STATE.ratingValuation
        }
      }
    case RESET_RECORD_OF_TITLE_DETAILS:
      return {
        ...state,
        recordOfTitleDetails: {
          ...INITIAL_STATE.recordOfTitleDetails
        }
      }
    case LOAD_DISCLAIMER_FINISHED: {
      return {
        ...state,
        disclaimer: {
          data: action.payload,
          status: {
            isError: false,
            loading: false,
            msg: null
          }
        }
      }
    }
    case LOAD_DISCLAIMER_FAILED: {
      return {
        ...state,
        disclaimer: {
          data: [],
          status: {
            isError: true,
            loading: false,
            msg: DISCLAIMER_PANEL_ERROR.replace('statusCode', action.payload)
          }
        }
      }
    }
    case LOAD_COPYRIGHT_FINISHED: {
      return {
        ...state,
        copyright: {
          data: action.payload,
          status: {
            isError: false,
            loading: false,
            msg: null
          }
        }
      }
    }
    case LOAD_COPYRIGHT_FAILED: {
      return {
        ...state,
        copyright: {
          data: [],
          status: {
            isError: true,
            loading: false,
            msg: COPYRIGHT_PANEL_ERROR.replace('statusCode', action.payload)
          }
        }
      }
    }
    case LOAD_PROPERTY_TIMELINE_FINISHED: {
      return {
        ...state,
        propertyTimeline: {
          data: action.payload,
          status: {
            isError: false,
            loading: false,
            msg: null
          }
        }
      }
    }
    case LOAD_PROPERTY_TIMELINE_FAILED:
      return {
        ...state,
        propertyTimeline: {
          data: {},
          status: {
            isError: true,
            loading: false,
            msg: PROPERTY_TIMELINE_PANEL_ERROR
          }
        }
      }
    case RESET_PROPERTY_TIMELINE:
      return {
        ...state,
        propertyTimeline: {
          ...INITIAL_STATE.propertyTimeline
        }
      }
    case LOAD_COMPARABLE_PROPERTIES_FINISHED:
      return {
        ...state,
        comparableProperties: {
          data: { ...action.payload },
          status: {
            ...state.comparableProperties.status,
            loading: false,
            msg: null
          }
        }
      }
    case LOAD_COMPARABLE_PROPERTIES_FAILED:
      return {
        ...state,
        comparableProperties: {
          ...state.comparableProperties,
          status: {
            isError: true,
            loading: !state.comparableProperties.status.loading,
            msg: COMPARABLE_PROPERTIES_PANEL_ERROR
          }
        }
      }
    case RESET_COMPARABLE_PROPERTIES:
      return {
        ...state,
        comparableProperties: {
          ...INITIAL_STATE.comparableProperties
        }
      }
    case LOAD_LOCAL_SCHOOLS_FINISHED:
      return {
        ...state,
        localSchools: {
          data: { ...action.payload },
          status: {
            ...state.localSchools.status,
            loading: false,
            msg: null
          }
        }
      }
    case LOAD_LOCAL_SCHOOLS_FAILED:
      return {
        ...state,
        localSchools: {
          ...state.localSchools,
          status: {
            isError: true,
            loading: !state.localSchools.status.loading,
            msg: LOCAL_SCHOOLS_PANEL_ERROR
          }
        }
      }
    case RESET_LOCAL_SCHOOLS:
      return {
        ...state,
        localSchools: {
          ...INITIAL_STATE.localSchools
        }
      }
    case LOAD_MARKET_INSIGHTS_FINISHED: {
      const { data, yearCount } = action.payload
      const { errors } = data
      let isError = false
      let msg = null

      if (!isEmpty(errors)) {
        if (errors.find(error => error.msg === RESPONSE_RESPONSE_MESSAGE.NO_CONTENT_MESSAGE)) {
          msg = MARKET_INSIGHTS_NOT_AVAILABLE
        } else {
          msg = MARKET_INSIGHTS_PANEL_ERROR
          isError = true
        }
      }

      return {
        ...state,
        marketInsights: {
          data,
          status: {
            isError,
            loading: false,
            msg
          },
          yearCount
        }
      }
    }
    case LOAD_MARKET_INSIGHTS_FAILED: {
      const { yearCount } = action.payload
      return {
        ...state,
        marketInsights: {
          data: {},
          status: {
            isError: true,
            loading: false,
            msg: MARKET_INSIGHTS_PANEL_ERROR
          },
          yearCount
        }
      }
    }
    case RESET_MARKET_INSIGHTS: {
      return {
        ...state,
        marketInsights: {
          ...INITIAL_STATE.marketInsights,
          yearCount: action.payload
        }
      }
    }
    case LOAD_MAP_FINISHED:
      return {
        ...state,
        mapPropertyCoordinates: {
          data: action.payload,
          status: {
            isError: false,
            loading: false,
            msg: null
          }
        }
      }
    case LOAD_MAP_FAILED:
      return {
        ...state,
        mapPropertyCoordinates: {
          ...state.mapPropertyCoordinates,
          status: {
            isError: true,
            loading: false,
            msg: MAP_PANEL_ERROR
          }
        }
      }
    case RESET_MAP:
      return {
        ...state,
        mapPropertyCoordinates: {
          ...INITIAL_STATE.mapPropertyCoordinates
        }
      }
    default:
      return state
  }
}
